import { loginPath } from '@components/modules/auth';
import { Box } from '@components/ui/atoms/Box';
import { Icon, LogoutBoldIcon, LogoutIcon } from '@components/ui/atoms/Icons';
import { Link } from '@components/ui/atoms/Link';
import { Text } from '@components/ui/atoms/Text';
import { Tooltip } from '@components/ui/molecules/Tooltip';
import { trackLogout } from '@shared/analytics';
import { useAuthContext } from '@shared/auth';
import { useBreakpointValue } from '@shared/chakra/chakra';
import { useHover } from '@shared/hooks';
import { useCallback } from 'react';
import { useNavigate } from 'react-router';

type Props = {
  isExpanded?: boolean;
};

export const Footer: React.FC<Props> = ({ isExpanded }) => {
  const { logout } = useAuthContext();
  const navigate = useNavigate();
  const isMobile = useBreakpointValue({ base: true, md: false });

  const { ref, isHovering } = useHover<HTMLDivElement>();

  const handleLogOut = useCallback(() => {
    trackLogout();
    void logout();
    void navigate(loginPath);
  }, [logout, navigate]);

  let paddingX = '0';

  if (isExpanded || isMobile) {
    paddingX = isMobile ? '2' : '4';
  }

  const logoutRow = (
    <Box
      ref={ref}
      display="flex"
      alignItems="center"
      justifyContent={isExpanded || isMobile ? 'flex-start' : 'center'}
      bottom="0"
      w="full"
      css={{ height: '40px' }}
      px={paddingX}
      borderRadius="8px"
      cursor="pointer"
      onClick={handleLogOut}
      aria-label={!isExpanded && !isMobile ? 'Logout' : undefined}
      transition="all 0.2s"
    >
      <Icon
        css={{
          height: isExpanded || isMobile ? '16px' : '18px',
          width: isExpanded || isMobile ? '16px' : '18px',
          flexShrink: '0',
        }}
      >
        {isHovering ? <LogoutBoldIcon /> : <LogoutIcon />}
      </Icon>
      {(isExpanded || isMobile) && (
        <Link textStyle="bodyThree" ml={isExpanded || isMobile ? '2' : '0'}>
          <Text
            fontSize={isMobile ? '12px' : '14px'}
            fontWeight={isHovering ? '700' : '500'}
            color={isHovering ? 'brand' : 'body.dark'}
          >
            Logout
          </Text>
        </Link>
      )}
    </Box>
  );

  if (!isExpanded && !isMobile) {
    return (
      <Tooltip content="Logout" positioning={{ placement: 'right' }} showArrow>
        <Box display="block" w="full">
          {logoutRow}
        </Box>
      </Tooltip>
    );
  }

  return logoutRow;
};
